import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {showAnalysisProgressTrayTask} from 'analysis-tool/progress/AnalysisProgressTrayTask';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import ArchiveButton from 'archive/components/buttons/ArchiveButton';
import UnarchiveButton from 'archive/components/buttons/UnarchiveButton';
import {isFilesEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as Context from 'Context';
import DatasetTableWrapper from 'data-frames/wrapper/DatasetTableWrapper';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FromServerRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import ParametersForPlateMapWrapper from 'notebook/integrations/procedures/ParametersForPlateMapWrapper';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import * as AssayRecordDAO from 'results/assays/AssayRecordDAO';
import {PlateRecord, PlateTransferType} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import PlateRecordSummary from 'samples/plate-maps/plate-record/PlateRecordSummary';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import AnalysisChip from 'ui-library/chips/AnalysisChip';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import DatasetChip from 'ui-library/chips/DatasetChip';
import {refreshChips} from 'ui-library/chips/useSmartChipQuery';
import CollapsibleCard from 'ui-library/CollapsibleCard';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ModalAnchor from 'ui-library/ModalAnchor';
import {withLoadingScreen} from 'util/Promises';

import {LocationGraphQL} from '../../../graphql-fragments/inventory/__generated/LocationGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {ResultBlobGraphQL} from '../../../graphql-fragments/results/__generated/ResultBlobGraphQL';
import {
  AutomationOutputFileQuery,
  AutomationOutputFileQueryVariables,
  AutomationOutputFileQuery_automationOutputFile_AutomationOutputFileNode_createdFile_FileNode as FileNode,
} from './__generated/AutomationOutputFileQuery';
import {LocationQuery, LocationQueryVariables} from './__generated/LocationQuery';
import {StorableSchemaQuery, StorableSchemaQueryVariables} from './__generated/StorableSchemaQuery';
import {setAutomationFileStatus} from './AutomationFileActions';
import * as AutomationFileDAO from './AutomationFileDAO';
import AutomationOutputFileFolderPicker from './AutomationOutputFileFolderPicker';
import AutomationOutputFileLocationPicker from './AutomationOutputFileLocationPicker';
import AutomationOutputFileTimezonePicker from './AutomationOutputFileTimezonePicker';
import FailedRunAutomationOutputFileButton from './FailedRunAutomationOutputFileButton';
import getStatusForOutputFileButton from './getStatusForOutputFileButton';
import {
  isFolderRequiredForOutputFile,
  isLocationRequiredForOutputFile,
} from './isOutputFileMissingFolderOrLocation';
import LabAutoResultsArchiveModal from './LabAutoResultsArchiveModal';
import PendingOrRunningRunAutomationOutputFileButton from './PendingOrRunningRunAutomationOutputFileButton';
import RequiredSkipRowsWithErrorsBanner from './RequiredSkipRowsWithErrorsBanner';
import SkipRowsWithErrorsInput from './SkipRowsWithErrorsInput';
import SucceededRunAutomationOutputFileButton from './SucceededRunAutomationOutputFileButton';

export const DISABLED_ARCHIVE_TOOLTIP_TEXT =
  'Only output files that produce result tables or datasets can be archived in the run';

export const STORABLE_SCHEMA_QUERY = gql`
  query StorableSchemaQuery($storableSchemaId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    storableSchema(id: $storableSchemaId) {
      ... on StorableSchemaInterface {
        ...StorableSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StorableSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const LOCATION_QUERY = gql`
  query LocationQuery($locationId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    location(id: $locationId) {
      ... on LocationNode {
        ...LocationGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Location').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const AUTOMATION_OUTPUT_FILE_QUERY = gql`
  query AutomationOutputFileQuery($automationOutputFileId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    automationOutputFile(id: $automationOutputFileId) {
      ... on AutomationOutputFileNode {
        id
        status
        transforms {
          id
          status
        }
        createdFile {
          ... on FileNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AutomationOutputFileChipWithArchiveActionProps {
  className?: string;
  automationOutputFile: AutomationOutputFileGraphQL & {blob: ResultBlobGraphQL};
  updateAutomationOutputFile: (automationOutputFile: AutomationOutputFileGraphQL) => void;
  isRemovable: boolean;
  runId: string;
  isArchivable?: boolean;
  openArchiveModal?: (automationOutputFileId: string, automationOutputFileName: string) => void;
  archiveRecord?: ArchiveRecord | null;
  createdFile?: FileNode | null;
}

export function AutomationOutputFileChipWithArchiveAction(
  props: AutomationOutputFileChipWithArchiveActionProps
): JSX.Element {
  const archiveAction = props.archiveRecord ? 'unarchive' : 'archive';

  const hasResult = props.automationOutputFile.automationOutputFileConfig.resultSchemaInfo != null;
  const hasDataset = Boolean(props.automationOutputFile.automationOutputFileConfig.shouldCreateDataset);
  // the text shouldn't ever display with 'objects' since the tooltip requires at least one of these to be present
  const innerText = hasResult
    ? hasDataset
      ? 'results and dataset'
      : 'results'
    : hasDataset
      ? 'dataset'
      : 'objects';
  const ENABLED_ARCHIVE_TOOLTIP_TEXT = `Archive output file and generated ${innerText}`;
  const ENABLED_UNARCHIVE_TOOLTIP_TEXT = `Unarchive output file and generated ${innerText}`;

  const archiveButtonTooltip = props.archiveRecord
    ? ENABLED_UNARCHIVE_TOOLTIP_TEXT
    : ENABLED_ARCHIVE_TOOLTIP_TEXT;

  async function doRemove(): Promise<void> {
    const response = await AutomationFileDAO.clearAutomationOutputFileBlob(props.automationOutputFile.id);
    props.updateAutomationOutputFile(response.automationOutputFile);
  }

  async function doUnarchive(): Promise<void> {
    await Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_RUN,
        },
      },
      null,
      async () => {
        const response = await withLoadingScreen(
          AssayRecordDAO.unarchiveAutomationOutputFile(props.automationOutputFile.id),
          'Unarchiving file...'
        );
        const chipIdsToRefresh: Array<string> = [];
        if (response.automationOutputFile.datasetId != null) {
          chipIdsToRefresh.push(response.automationOutputFile.datasetId);
        }
        if (response.automationOutputFile.analysisId != null) {
          chipIdsToRefresh.push(response.automationOutputFile.analysisId);
        }
        refreshChips(chipIdsToRefresh);
        props.updateAutomationOutputFile(response.automationOutputFile);
      }
    );
  }

  return (
    <div className={css(styles.outputFileViewerRow)}>
      {isFilesEnabled() && props.createdFile ? (
        <BenchChip
          className={classNames('chip-container', css(styles.outputProcessorChip), props.className)}
          id={props.createdFile.id}
          size={ChipSize.SMALL}
          onRemove={props.isRemovable ? doRemove : null}
        />
      ) : (
        <Chip
          className={classNames('chip-container', css(styles.outputProcessorChip), props.className)}
          icon="paperclip"
          size={ChipSize.MINI}
          text={props.automationOutputFile.blob.name}
          href={props.automationOutputFile.blob.url}
          onRemove={props.isRemovable ? doRemove : null}
        />
      )}
      {!props.isRemovable &&
        (props.archiveRecord ? (
          <UnarchiveButton
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={async () => {
              await doUnarchive();
            }}
          />
        ) : (
          <ArchiveButton
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            items={[props.automationOutputFile]}
            onClick={() => {
              if (props.openArchiveModal != null && archiveAction === 'archive' && props.isArchivable) {
                if (isFilesEnabled() && props.createdFile) {
                  props.openArchiveModal(
                    props.automationOutputFile.id,
                    `${innerText} created by file ${props.automationOutputFile.blob.name}`
                  );
                } else {
                  props.openArchiveModal(props.automationOutputFile.id, props.automationOutputFile.blob.name);
                }
              }
            }}
            disabled={!props.isArchivable}
            tooltipTitle={props.isArchivable ? archiveButtonTooltip : DISABLED_ARCHIVE_TOOLTIP_TEXT}
          />
        ))}
    </div>
  );
}

function OutputTableShowArchivedFieldsToggle(props: {
  numArchivedFiles: number;
  showArchivedFiles: boolean;
  setShowArchivedFiles: (b: boolean) => void;
}): JSX.Element | null {
  return props.numArchivedFiles ? (
    <a
      href=""
      onClick={(e) => {
        e.preventDefault();
        props.setShowArchivedFiles(!props.showArchivedFiles);
      }}
      {...getTestProps({component: 'ShowArchivedFilesToggle', element: 'showArchivedFilesToggle'})}
    >
      {props.showArchivedFiles ? 'Hide' : 'Show'} archived outputs ({props.numArchivedFiles})
    </a>
  ) : null;
}

function AutomationOutputHeader(props: {
  tableName: string;
  numArchivedFiles: number;
  showArchivedFiles: boolean;
  setShowArchivedFiles: (b: boolean) => void;
}): JSX.Element {
  return (
    <Row>
      <hr />
      <Col md={6}>
        <FieldLabel
          className="resultsAttachment-assayRun-fieldHeader u-tiny-spacing-bottom"
          label={`Output: ${props.tableName}`}
        />
      </Col>
      <Col md={6} className={css(styles.showArchivedFilesButtonContainer)}>
        <OutputTableShowArchivedFieldsToggle
          numArchivedFiles={props.numArchivedFiles}
          showArchivedFiles={props.showArchivedFiles}
          setShowArchivedFiles={props.setShowArchivedFiles}
        />
      </Col>
    </Row>
  );
}

interface BaseRunAutomationOutputFileButtonProps {
  automationOutputFile: AutomationOutputFileGraphQL;
  isStale: boolean;
  runId?: string | null;
  configName: string | null;
  locked?: boolean;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationOutputFiles?: (
    automationOutputFiles: Array<AutomationOutputFileGraphQL>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  fileId?: string;
  previousFileNames: Set<string>;
}

interface AutomationOutputFileViewerProps {
  automationOutputFile: AutomationOutputFileGraphQL;
  renderTable: (automationOutputFile: AutomationOutputFileGraphQL) => JSX.Element | null;
  locked: boolean;
  runId: string | null;
  configName: string | null;
  previousFileNames: Set<string>;
  openArchiveModal: (automationOutputFileId: string, automationOutputFileName: string) => void;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationOutputFiles: (
    automationOutputFiles: Array<AutomationOutputFileGraphQL>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  viewer?: boolean;
}

interface QueryProps {
  storableSchema: StorableSchema | null;
  location: LocationGraphQL | null;
  liveStatus: AutomationFileStatusGraphQL;
  createdFile: FileNode | null;
}

export const REFETCH_STATUS_INTERVAL_MS = 60000; // 1 minute
export const INITIAL_REFRESH_STATUS_INTERVAL_MS = 500; // 0.5 sec

export function AutomationOutputFileViewer(props: AutomationOutputFileViewerProps & QueryProps): JSX.Element {
  const {automationOutputFile} = props;
  const versionedStatus = getStatusForOutputFileButton(automationOutputFile);

  let StatusButtonClass: React.ComponentType<BaseRunAutomationOutputFileButtonProps>;
  switch (props.liveStatus) {
    case AutomationFileStatusGraphQL.pending:
    case AutomationFileStatusGraphQL.running:
      StatusButtonClass = PendingOrRunningRunAutomationOutputFileButton;
      break;
    case AutomationFileStatusGraphQL.succeeded:
      StatusButtonClass = SucceededRunAutomationOutputFileButton;
      break;
    case AutomationFileStatusGraphQL.failed:
      StatusButtonClass = FailedRunAutomationOutputFileButton;
      break;
    default:
      throw new Error('Unexpected automation output file status');
  }

  const updateAutomationOutputFile = (
    outputFile: AutomationOutputFileGraphQL,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ): void => {
    props.updateAutomationOutputFiles([outputFile], structuredTables);
  };

  return (
    <>
      {BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING &&
        versionedStatus === AutomationFileStatusGraphQL.pending &&
        automationOutputFile?.skipRowsWithErrorsRequired && (
          <RequiredSkipRowsWithErrorsBanner automationOutputFile={automationOutputFile} />
        )}
      <Row bufferRowReset={true} className="resultsAttachment-assayRun-automationFileViewer">
        <Col className={css(styles.buttonSection)} sm={5}>
          <Row
            className="u-justify-content-start"
            bufferRowReset={true}
            dataTestAttrs={{component: 'RunAutomationOutputFileViewer', element: 'outputFileRow'}}
          >
            <Col md={3}>
              <span className="fieldLabel-sublabel">Output file</span>
            </Col>
            <Col md={9}>
              <AutomationOutputFileChipWithArchiveAction
                automationOutputFile={
                  automationOutputFile as AutomationOutputFileGraphQL & {blob: ResultBlobGraphQL}
                }
                updateAutomationOutputFile={updateAutomationOutputFile}
                isArchivable={
                  (automationOutputFile.automationOutputFileConfig.resultSchemaInfo != null ||
                    automationOutputFile.automationOutputFileConfig.shouldCreateDataset) &&
                  automationOutputFile.automationOutputFileConfig.destinationInfo == null &&
                  automationOutputFile.automationOutputFileConfig.tagSchemaInfo == null
                }
                isRemovable={[
                  AutomationFileStatusGraphQL.pending,
                  AutomationFileStatusGraphQL.failed,
                ].includes(automationOutputFile.status)}
                runId={props.runId!}
                openArchiveModal={props.openArchiveModal}
                archiveRecord={automationOutputFile.archiveRecord}
                className={automationOutputFile.archiveRecord ? 'chip--faded' : 'chip-container'}
                createdFile={props.createdFile}
              />
              {BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING && (
                <SkipRowsWithErrorsInput
                  automationOutputFile={automationOutputFile}
                  updateAutomationOutputFile={updateAutomationOutputFile}
                />
              )}
            </Col>
          </Row>
          {isFolderRequiredForOutputFile(automationOutputFile) && (
            <AutomationOutputFileFolderPicker
              runId={props.runId}
              updateAutomationOutputFile={updateAutomationOutputFile}
              storableSchema={props.storableSchema}
              locked={props.locked || !props.runId}
              automationOutputFile={automationOutputFile}
            />
          )}
          {isLocationRequiredForOutputFile(automationOutputFile) && (
            <AutomationOutputFileLocationPicker
              runId={props.runId}
              updateAutomationOutputFile={updateAutomationOutputFile}
              storableSchema={props.storableSchema}
              location={props.location}
              locked={props.locked || !props.runId}
              automationOutputFile={automationOutputFile}
            />
          )}
          {automationOutputFile.canConfigureTimezone ? (
            <AutomationOutputFileTimezonePicker
              runId={props.runId}
              updateAutomationOutputFile={updateAutomationOutputFile}
              locked={props.locked || !props.runId}
              automationOutputFile={automationOutputFile}
            />
          ) : null}
          <Row
            className="u-justify-content-start"
            bufferRowReset={true}
            dataTestAttrs={{
              component: 'RunAutomationOutputFileViewer',
              element: 'automationOutputFileViewerRow',
            }}
          >
            <Col md={3}>
              <span className="fieldLabel-sublabel">Status</span>
            </Col>
            <Col md={9}>
              <EditorPropertiesContext.Consumer>
                {({fileId}) => (
                  <StatusButtonClass
                    automationOutputFile={automationOutputFile}
                    isStale={props.liveStatus !== getStatusForOutputFileButton(automationOutputFile)}
                    runId={props.runId}
                    configName={props.configName}
                    locked={props.locked || !props.runId}
                    handleRefreshVersionedData={props.handleRefreshVersionedData}
                    updateAutomationOutputFiles={props.updateAutomationOutputFiles}
                    previousFileNames={props.previousFileNames}
                    fileId={fileId}
                  />
                )}
              </EditorPropertiesContext.Consumer>
            </Col>
          </Row>
          {automationOutputFile.datasetId && (
            <Row
              className="u-justify-content-start"
              bufferRowReset={true}
              dataTestAttrs={{component: 'RunAutomationOutputFileViewer', element: 'datasetRow'}}
            >
              <Col sm={3}>
                <span className="fieldLabel-sublabel">Dataset</span>
              </Col>
              <Col md={9}>
                <DatasetChip
                  className={css(styles.outputProcessorChip)}
                  id={automationOutputFile.datasetId}
                  size={ChipSize.SMALL}
                />
              </Col>
            </Row>
          )}
          {automationOutputFile.analysisId && (
            <Row
              className="u-justify-content-start"
              bufferRowReset={true}
              dataTestAttrs={{component: 'RunAutomationOutputFileViewer', element: 'analysisRow'}}
            >
              <Col md={3}>
                <span className="fieldLabel-sublabel">Analysis</span>
              </Col>
              <Col md={9}>
                <AnalysisChip
                  className={css(styles.outputProcessorChip)}
                  id={automationOutputFile.analysisId}
                  size={ChipSize.SMALL}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      {automationOutputFile.plateRecordsJson && (
        <Row
          className="u-justify-content-start"
          bufferRowReset={true}
          dataTestAttrs={{component: 'RunAutomationOutputFileViewer', element: 'analysisRow'}}
        >
          {/* Custom column width for this to line up with the section above, where similar labels have a
          width of 1/4 of a row, within a column that itself is 5/12 of the whole screen, equivalent to
          1.25/12 of the full-width area this row takes up.  */}
          <Col className={css(styles.plateRecordSectionLabel)} sm={1}>
            <span className="fieldLabel-sublabel">Plate Records</span>
          </Col>
          <Col md={11}>
            <ParametersForPlateMapWrapper>
              {({isInProcedure, plateMaterialInputs, procedureStudyDesignType}) => (
                <EditorPropertiesContext.Consumer>
                  {({fileId}) => (
                    <>
                      {(automationOutputFile.plateRecordsJson ?? []).map((plateRecord: PlateRecord) => (
                        <div className={css(styles.plateRecordCard)}>
                          <CollapsibleCard
                            leftHeader={
                              <div>
                                <span
                                  className={css(styles.plateRecordHeader)}
                                >{`Plate ${plateRecord.transferType === PlateTransferType.CREATE ? 'created' : 'updated'}`}</span>
                                <BenchChip id={plateRecord.plateApiId} size={ChipSize.TABLE_CELL} />
                              </div>
                            }
                            initiallyExpanded={false}
                          >
                            <div className={css(styles.plateRecordContent)}>
                              <PlateRecordSummary
                                key={plateRecord.id}
                                plateRecord={plateRecord}
                                procedureStudyDesignType={procedureStudyDesignType}
                                analyticsMetadata={{
                                  plateId: plateRecord.plateApiId,
                                  transferRecordId: plateRecord.id,
                                  actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_RUN,
                                  fileId: fileId ?? '',
                                }}
                              />
                            </div>
                          </CollapsibleCard>
                        </div>
                      ))}
                    </>
                  )}
                </EditorPropertiesContext.Consumer>
              )}
            </ParametersForPlateMapWrapper>
          </Col>
        </Row>
      )}
      {/* do not show dataset preview if output file is archived */}
      {automationOutputFile.datasetId && automationOutputFile.archiveRecord == null && (
        <DatasetTableWrapper
          id={automationOutputFile.datasetId}
          actionOrigin={ActionOrigin.RUN_PAGE}
          autoCollapseRowLimit={BenchlingConfig.ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE}
        />
      )}
      {props.renderTable(automationOutputFile)}
    </>
  );
}

function QueryingAutomationOutputFileViewer(
  props: AutomationOutputFileViewerProps & {refreshCounter?: number}
): JSX.Element {
  // Track if the AOF was manually updated, so we know if it makes sense to show the analysis progress bar
  const [updateTriggered, setUpdateTriggered] = useState<boolean>(false);
  const storableSchemaId =
    props.automationOutputFile?.automationOutputFileConfig.destinationInfo?.storableSchema?.id;
  const storableSchemaResult = useSkippableBenchlingQuery<StorableSchemaQuery, StorableSchemaQueryVariables>(
    STORABLE_SCHEMA_QUERY,
    {
      variables: {
        storableSchemaId: storableSchemaId!,
      },
      skip: storableSchemaId == null,
    }
  );
  const locationId = props.automationOutputFile?.locationId;
  const locationResult = useSkippableBenchlingQuery<LocationQuery, LocationQueryVariables>(LOCATION_QUERY, {
    variables: {
      locationId: locationId!,
    },
    skip: locationId == null,
  });
  const automationOutputFileId = props.automationOutputFile?.id;
  const outputFileResult = useSkippableBenchlingQuery<
    AutomationOutputFileQuery,
    AutomationOutputFileQueryVariables
  >(AUTOMATION_OUTPUT_FILE_QUERY, {
    variables: {
      automationOutputFileId,
    },
    skip: automationOutputFileId == null || automationOutputFileId === '',
  });
  const liveOutputFile =
    outputFileResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(outputFileResult.data.automationOutputFile)
      ? outputFileResult.data.automationOutputFile
      : null;
  const versionedStatus = getStatusForOutputFileButton(props.automationOutputFile);
  const createdFile =
    (liveOutputFile?.createdFile && !isInaccessible(liveOutputFile?.createdFile)
      ? liveOutputFile?.createdFile
      : null) ?? null;
  const liveStatus = liveOutputFile ? getStatusForOutputFileButton(liveOutputFile) : versionedStatus;
  const refetchLiveOutputFile = useCallback(async () => {
    if (!(automationOutputFileId == null || automationOutputFileId === '')) {
      await outputFileResult.refetch({automationOutputFileId});
    }
  }, [automationOutputFileId, outputFileResult]);

  // Keep a ref to the initial/last-handled tick of the refresh counter since we cannot have a useEffect
  // that depends purely on refreshCounter but not refetchLiveInputFile
  const lastRefreshCounter = useRef(0);
  useEffect(() => {
    // Refetch live status periodically, and also immediately when Run data is refreshed, so it does not fall
    // behind the persisted status.
    if (props.refreshCounter && props.refreshCounter > lastRefreshCounter.current) {
      lastRefreshCounter.current = props.refreshCounter;
      refetchLiveOutputFile();
    }
    // Refetch status every minute once running or complete.
    const intervalId = setInterval(() => {
      if (versionedStatus !== AutomationFileStatusGraphQL.succeeded) {
        refetchLiveOutputFile();
      }
    }, REFETCH_STATUS_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [versionedStatus, refetchLiveOutputFile, props.refreshCounter]);

  useEffect(() => {
    // When an output file is first inserted from a Connection, we expect it to start processing automatically
    // and want to update the button rapidly if so. To accomplish this, re-run the status query a few times
    // rapidly and then stop.
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        if (versionedStatus === AutomationFileStatusGraphQL.pending) {
          refetchLiveOutputFile();
          setUpdateTriggered(true);
        }
      }, INITIAL_REFRESH_STATUS_INTERVAL_MS * i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.automationOutputFile.analysisId != null && updateTriggered) {
      showAnalysisProgressTrayTask(props.automationOutputFile.analysisId);
    }
  }, [props.automationOutputFile.analysisId, updateTriggered]);

  // Store the `props.updateAutomationOutputFile` in a ref to ensure that when it is called after the
  // onClick's `await` that we are using the latest version of this callback. This is important to do
  // as the function `props.updateAutomationOutputFile` closures the current full set of automation
  // output files, so if multiple copies of this component are clicked concurrently, they would
  // otherwise reset each other's updates.
  const updateHandlerRef = useRef<AutomationOutputFileViewerProps['updateAutomationOutputFiles']>(
    props.updateAutomationOutputFiles
  );
  updateHandlerRef.current = props.updateAutomationOutputFiles;
  useEffect(() => {
    setAutomationFileStatus(automationOutputFileId, liveStatus);
  }, [automationOutputFileId, liveStatus]);
  return (
    <AutomationOutputFileViewer
      {...props}
      storableSchema={
        storableSchemaResult.type === QueryResultType.SUCCESS &&
        !isInaccessible(storableSchemaResult.data.storableSchema)
          ? graphQLToREST('StorableSchema', storableSchemaResult.data.storableSchema)
          : null
      }
      location={
        locationResult.type === QueryResultType.SUCCESS && !isInaccessible(locationResult.data.location)
          ? locationResult.data.location
          : null
      }
      liveStatus={liveStatus}
      updateAutomationOutputFiles={async (outputFiles, structuredTables) => {
        setUpdateTriggered(true);
        await refetchLiveOutputFile();
        updateHandlerRef.current(outputFiles, structuredTables);
      }}
      createdFile={createdFile}
    />
  );
}
interface RunAutomationOutputFileViewerProps {
  automationOutputFiles: Array<AutomationOutputFileGraphQL>;
  renderTable: (automationOutputFile: AutomationOutputFileGraphQL) => JSX.Element | null;
  runId: string | null;
  locked: boolean;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
  openArchiveModal: (automationOutputFileId: string, automationOutputFileName: string) => void;
  closeArchiveModal: () => void;
  isArchiveModalOpen: boolean;
  archiveAutomationOutputFileId: string;
  archiveAutomationOutputFileName: string;
  viewer?: boolean;
  configName: string;
  previousFileNames: Set<string>;
  hasSucceededAutomationOutputFiles: boolean;
  refreshCounter?: number;
}

/**
 * Renders an AutomationOutputFile and any associated structured table in a lab automation run.
 * Includes ability to un/archive an AutomationOutputFile if it has results associated with it.
 */
export default function RunAutomationOutputFileViewer(
  props: RunAutomationOutputFileViewerProps
): JSX.Element | null {
  const [showArchivedFiles, setShowArchivedFiles] = useState(false);
  if (props.automationOutputFiles.length === 0) {
    return null;
  }

  const unarchivedFiles = props.automationOutputFiles.filter(
    (automationOutputFile: AutomationOutputFileGraphQL) => automationOutputFile.archiveRecord == null
  );
  const numArchivedFiles = props.automationOutputFiles.length - unarchivedFiles.length;

  const updateAutomationOutputFile = (outputFile: AutomationOutputFileGraphQL): void => {
    props.updateAutomationOutputFiles([outputFile]);
  };

  return (
    <>
      <AutomationOutputHeader
        tableName={props.configName}
        numArchivedFiles={numArchivedFiles}
        showArchivedFiles={showArchivedFiles}
        setShowArchivedFiles={setShowArchivedFiles}
      />

      <ModalAnchor key="archive-modal" open={props.isArchiveModalOpen}>
        <LabAutoResultsArchiveModal
          fileName={props.archiveAutomationOutputFileName}
          onRequestClose={props.closeArchiveModal}
          automationOutputFileId={props.archiveAutomationOutputFileId}
          updateAutomationOutputFile={updateAutomationOutputFile}
          analyticsMetadata={{actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_RUN}}
        />
      </ModalAnchor>
      {(showArchivedFiles ? props.automationOutputFiles : unarchivedFiles).map(
        (automationOutputFile: AutomationOutputFileGraphQL) =>
          automationOutputFile?.blob != null && (
            <QueryingAutomationOutputFileViewer
              key={automationOutputFile.id}
              automationOutputFile={automationOutputFile}
              renderTable={props.renderTable}
              locked={props.locked}
              runId={props.runId}
              configName={props.configName}
              previousFileNames={props.previousFileNames}
              openArchiveModal={props.openArchiveModal}
              handleRefreshVersionedData={props.handleRefreshVersionedData}
              updateAutomationOutputFiles={props.updateAutomationOutputFiles}
              viewer={props.viewer}
              refreshCounter={props.refreshCounter}
            />
          )
      )}
    </>
  );
}

const styles = StyleSheet.create({
  showArchivedFilesButtonContainer: {
    textAlign: 'right',
    justifyContent: 'flex-end',
    marginBottom: '10px',
  },
  outputProcessorChip: {
    width: '100%',
  },
  buttonSection: {
    float: 'none',
  },
  outputFileViewerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  plateRecordCard: {
    marginBottom: '10px',
  },
  plateRecordContent: {
    padding: '6px 0px',
  },
  plateRecordHeader: {
    marginRight: '6px',
  },
  plateRecordSectionLabel: {
    width: '10.4166666667%',
  },
});
